import { FC, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useTranslation } from "react-i18next"

import { useTimezone } from "hooks/useTimezone"

import { GlobalDuplicateMatch } from "services/risk/types"

interface Props {
  match: GlobalDuplicateMatch
  onClose: () => void
  onReview: (matchId: string, decision: string) => Promise<void>
}

const CRITERIA_LABELS: Record<string, string> = {
  email: "Email",
  ip_address: "IP Address",
  device_fingerprint: "Device",
  phone_number: "Phone",
  full_name: "Full Name",
  date_of_birth: "Date of Birth",
  payment_method: "Payment Method"
}

export const DuplicateMatchModal: FC<Props> = ({ match, onClose, onReview }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const navigate = useNavigate()
  const { formatDate } = useTimezone()
  const [loading, setLoading] = useState(false)

  const handleDecision = async (decision: string) => {
    setLoading(true)
    await onReview(match.id, decision)
    setLoading(false)
    onClose()
  }

  const scorePercent = Math.round(match.similarity_score * 100)
  const criteriaScores = match.criteria_scores as Record<string, number>
  const metaItems = [
    { label: t("risk.duplicateModal.metaSeverity"), value: match.severity },
    { label: t("risk.duplicateModal.metaStatus"), value: match.review_status },
    match.detected_at ? { label: t("risk.duplicateModal.metaDetected"), value: formatDate(match.detected_at) } : null
  ].filter(Boolean) as { label: string; value: string }[]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="relative w-full max-w-xl rounded-xl bg-base-200 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-base-content">{t("risk.duplicateModal.title")}</h3>
          <button className="btn btn-ghost btn-xs btn-circle" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Similarity bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-base-content/60 mb-1">
            <span>{t("risk.duplicateModal.similarityScore")}</span>
            <span className="font-semibold text-base-content">{scorePercent}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-base-300 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                scorePercent >= 80 ? "bg-error" : scorePercent >= 50 ? "bg-warning" : "bg-success"
              }`}
              style={{ width: `${scorePercent}%` }}
            />
          </div>
        </div>

        {/* Players */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[match.player_a, match.player_b].map((p, i) => (
            <div key={p.id} className="rounded-lg bg-base-300 p-3">
              <div className="text-xs text-base-content/50 mb-1">
                {t(i === 0 ? "risk.duplicateModal.playerA" : "risk.duplicateModal.playerB")}
              </div>
              <div className="text-sm font-medium text-primary cursor-pointer hover:underline" onClick={() => navigate(`/players/${p.id}`)}>
                {p.username ?? "—"}
              </div>
              <div className="text-xs text-base-content/60 truncate">{p.email ?? "—"}</div>
              <div className="text-xs text-base-content/40 mt-0.5">{p.id}</div>
            </div>
          ))}
        </div>

        {/* Criteria grid */}
        {Object.keys(criteriaScores).length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-base-content/50 mb-2">{t("risk.duplicateModal.matchedCriteria")}</div>
            <div className="grid grid-cols-2 gap-1">
              {Object.entries(criteriaScores).map(([key, score]) => (
                <div key={key} className="flex items-center justify-between rounded bg-base-300 px-2 py-1">
                  <span className="text-xs">{CRITERIA_LABELS[key] ?? key}</span>
                  <span className={`text-xs font-semibold ${score >= 0.8 ? "text-error" : score >= 0.5 ? "text-warning" : "text-success"}`}>
                    {Math.round(score * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="flex gap-4 text-xs text-base-content/50 mb-5">
          {metaItems.map((item) => (
            <span key={item.label}>
              {item.label}: <span className="text-base-content/80 font-medium">{item.value}</span>
            </span>
          ))}
        </div>

        {/* Actions */}
        {match.review_status === "PENDING" && (
          <div className="flex gap-2 justify-end">
            <button className="btn btn-sm btn-ghost" disabled={loading} onClick={() => handleDecision("DISMISSED")}>
              {t("risk.duplicateModal.dismiss")}
            </button>
            <button className="btn btn-sm btn-error" disabled={loading} onClick={() => handleDecision("CONFIRMED")}>
              {t("risk.duplicateModal.confirm")}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
